import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Monitor } from 'lucide-react';

interface SecurityLockOverlayProps {
  mode: 'screen' | 'fullscreen-exit';
  onSubmit: (pin: string) => Promise<void> | void;
  onCancel?: () => void;
  userLabel?: string;
  errorMessage?: string;
}

export default function SecurityLockOverlay({
  mode,
  onSubmit,
  onCancel,
  userLabel,
  errorMessage,
}: SecurityLockOverlayProps) {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPin('');
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timeout);
  }, [mode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      await onSubmit(pin);
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  const now = new Date();
  const title = mode === 'screen' ? 'MapleOS is locked' : 'PIN required to exit full screen';
  const description = mode === 'screen'
    ? 'Enter your MapleOS PIN to unlock this session.'
    : 'This MapleOS session is locked to full screen. Enter the PIN to leave full screen mode.';

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_40%),radial-gradient(circle_at_bottom,rgba(220,38,38,0.16),transparent_38%)]" />
      <Card className="relative w-full max-w-md border-white/10 bg-slate-950/95 text-white shadow-2xl">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/5">
            {mode === 'screen' ? <Lock className="h-7 w-7 text-emerald-300" /> : <Monitor className="h-7 w-7 text-amber-300" />}
          </div>
          <div>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription className="mt-2 text-slate-300">{description}</CardDescription>
          </div>
          <div className="space-y-1 text-sm text-slate-400">
            <div>{now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
            <div>{now.toLocaleDateString()}</div>
            {userLabel ? <div>Signed in as {userLabel}</div> : null}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mapleos-lock-pin" className="text-slate-200">PIN</Label>
              <Input
                ref={inputRef}
                id="mapleos-lock-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 8))}
                className="border-white/15 bg-black/40 text-center text-lg tracking-[0.35em] text-white placeholder:text-slate-500"
                placeholder="••••"
                maxLength={8}
              />
            </div>

            {errorMessage ? <p className="text-sm text-red-300">{errorMessage}</p> : null}

            <div className="flex gap-3">
              {onCancel ? (
                <Button type="button" variant="outline" className="flex-1 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white" onClick={onCancel}>
                  Cancel
                </Button>
              ) : null}
              <Button type="submit" className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500" disabled={submitting || pin.length < 4}>
                {mode === 'screen' ? 'Unlock' : 'Exit Full Screen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
